import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, TextField, TextareaField } from "@atlas/ui/web";
import type { BeneficioDaConsignataria, PreviaEncerramento } from "@atlas/sdk";
import { atlas } from "../../../lib/sdk";
import { mensagemDeErro } from "../../../lib/erro-validacao";
import { mesAno } from "../../../lib/beneficios";

// ENCERRAR UM BENEFÍCIO PELO PORTAL (#317).
//
// Antes de confirmar, a consignatária vê o que o encerramento faz na margem do
// servidor e a partir de quando — a prévia vem da API, pela mesma regra do órgão
// que o motor usa. Com pedido de cancelamento aberto, o pedido é atendido junto.
// A inclusão que ainda espera o servidor é retirada pelo mesmo botão.
//
// O mesmo modal serve à fila de pedidos (#318), com as rotas dela: a prévia e a
// confirmação chegam por parâmetro, e a tela é uma só.

export const competenciaOk = (c: string) => /^\d{4}(0[1-9]|1[0-2])$/.test(c);
export const motivoOk = (m: string) => m.trim().length >= 5;

type Resultado = { competenciaFim?: string; pendenciaEncerrada?: boolean; movimentoDesatualizado?: boolean };

/** O detalhe do benefício: as rotas do próprio benefício. */
export function EncerrarBeneficioModal({
  beneficio,
  onFechar,
  onFeito,
}: {
  beneficio: BeneficioDaConsignataria;
  onFechar: () => void;
  onFeito: (aviso: string) => void;
}) {
  const retira = beneficio.situacao === "aguardando_autorizacao";
  return (
    <EncerramentoModal
      chave={`beneficio:${beneficio.id}`}
      titulo={retira ? "Retirar inclusão" : "Encerrar benefício"}
      rotuloConfirmar={retira ? "Retirar" : "Encerrar"}
      retira={retira}
      descricao={`${beneficio.rotulo} de ${beneficio.nome ?? `matrícula ${beneficio.matricula}`} — ${beneficio.orgao}.`}
      carregarPrevia={(fim) => atlas.banco.beneficios.previaEncerramento(beneficio.id, fim)}
      confirmar={(competenciaFim, motivo) => atlas.banco.beneficios.encerrar(beneficio.id, { competenciaFim, motivo })}
      onFechar={onFechar}
      onFeito={onFeito}
    />
  );
}

export function EncerramentoModal({
  chave,
  titulo,
  rotuloConfirmar,
  retira = false,
  descricao,
  carregarPrevia,
  confirmar,
  onFechar,
  onFeito,
}: {
  /** Identifica a prévia no cache: um benefício, ou um pedido. */
  chave: string;
  titulo: string;
  rotuloConfirmar: string;
  retira?: boolean;
  descricao: string;
  carregarPrevia: (competenciaFim?: string) => Promise<PreviaEncerramento>;
  confirmar: (competenciaFim: string, motivo: string) => Promise<Resultado>;
  onFechar: () => void;
  onFeito: (aviso: string) => void;
}) {
  const qc = useQueryClient();
  const [fim, setFim] = useState("");
  const [motivo, setMotivo] = useState("");

  // A primeira prévia, sem competência, traz o fim padrão; o campo nasce com ele.
  const previa = useQuery({
    queryKey: ["banco", "previa-encerramento", chave, competenciaOk(fim) ? fim : ""],
    queryFn: () => carregarPrevia(competenciaOk(fim) ? fim : undefined),
    enabled: fim === "" || competenciaOk(fim),
    placeholderData: keepPreviousData,
    retry: false,
  });
  // Uma vez só: depois disso o campo é do operador — apagar para digitar outra
  // competência não pode trazer o padrão de volta.
  const [padraoAplicado, setPadraoAplicado] = useState(false);
  useEffect(() => {
    if (padraoAplicado || !previa.data) return;
    setPadraoAplicado(true);
    setFim(previa.data.competenciaFimPadrao);
  }, [padraoAplicado, previa.data]);

  const p = previa.data && previa.data.competenciaFim === fim ? previa.data : null;
  const valido = competenciaOk(fim) && motivoOk(motivo) && p !== null && !p.cortePassou;

  const salvar = useMutation({
    mutationFn: () => confirmar(fim, motivo.trim()),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["banco", "beneficios"] });
      void qc.invalidateQueries({ queryKey: ["banco", "operadora"] });
      void qc.invalidateQueries({ queryKey: ["banco", "pedidos-cancelamento"] });
      const base = r.pendenciaEncerrada
        ? "Inclusão retirada: ela não chega a descontar."
        : `Encerrado: último desconto em ${mesAno(r.competenciaFim ?? fim)}.`;
      onFeito(r.movimentoDesatualizado ? `${base} O movimento desta competência já foi emitido: a operação só vale numa retificação.` : base);
      onFechar();
    },
  });

  return (
    <Modal
      open
      danger
      title={titulo}
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Encerrando…" : rotuloConfirmar}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0 }}>{descricao}</p>
        {retira ? null : (
          <TextField
            label="Último desconto em"
            value={fim}
            placeholder="AAAAMM"
            hint={p ? `A partir de ${mesAno(p.aPartirDe)} o benefício não desconta mais.` : "A competência seguinte já sai sem o desconto."}
            onChange={(e) => setFim(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
        )}

        {/* A PRÉVIA: o que o encerramento faz na margem, pela regra do órgão. */}
        <section aria-label="Prévia do encerramento" style={{ padding: 12, borderRadius: 10, background: "var(--bg-elev-2)", display: "grid", gap: 6 }}>
          {previa.isError ? (
            <span role="alert">{mensagemDeErro(previa.error, "Não foi possível calcular a prévia.")}</span>
          ) : p ? (
            <>
              <strong>{p.mensagem}</strong>
              {p.cortePassou ? (
                <span role="alert" style={{ color: "var(--danger-500)" }}>
                  O corte de {mesAno(p.aPartirDe)} já passou: escolha um último desconto mais adiante.
                </span>
              ) : null}
              {p.atendePedido ? <span>O pedido de cancelamento do servidor será marcado como atendido.</span> : null}
            </>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>{competenciaOk(fim) || fim === "" ? "Calculando…" : "Informe a competência (AAAAMM)."}</span>
          )}
        </section>

        <TextareaField label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} hint="Fica no histórico do benefício." />
        {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível encerrar.")}</p>}
      </div>
    </Modal>
  );
}
